/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { GetStaticPaths, GetStaticProps, NextLayoutComponentType } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import MainLayout from '@/layouts/main';
import { localeList } from '@/public/locales/locale-list';
import { Api } from '@/api';
import { IReadSurah } from '@/infrastructure/interfaces/i-read-surah';
import { allRecordsPaginationOptions } from '@/infrastructure/const/pagination';
import { ReadSurah } from '@/infrastructure/class/read-surah';
import { IBreadcrumb } from '@/components/common/breadcrumbs';
import { routes } from '@/infrastructure/const/routes';
import { useRouter } from 'next/router';
import s from './styles.module.scss';
import { TurnLeftRounded } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import AppTitle from '@/components/common/title';
import AppContainer from '@/components/layout/container';
import Image from 'next/image';
import { replaceWords } from '@/utils/helpers/replace-words';
import Link from 'next/link';
import {
  BreadcrumbContext,
  IBreadcrumbContextType,
} from '@/infrastructure/context/breadcrumb-context';
import { decryptPageProps } from '@/utils/crypto/page-props-decrypt';
import { IEncryptedProps } from '@/infrastructure/interfaces/i-encrypted-props';
import { getLocaleForUrl } from '@/infrastructure/functions/get-locale-for-url';
import SeoHead from '@/components/common/seo-head';
import {
  baseMarkupSchema,
  breadcrumbListMarkupSchema,
  organizationMarkupSchema,
  webPageMarkupSchema,
  webSiteMarkupSchema,
} from '@/infrastructure/markup-schemas';

const api = Api.getInstance();
const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL;

const isValidSlug = (slug: unknown): slug is string =>
  typeof slug === 'string' && slug.trim().length > 0;

interface ISurahReadDetailProps {
  readSurah: IReadSurah;
  ayasCount: number;
}

const SurahReadHistory: NextLayoutComponentType<IEncryptedProps> = (props) => {
  const { data } = props;
  const decrypted = decryptPageProps<ISurahReadDetailProps>(data);
  const { readSurah, ayasCount } = decrypted;

  const router = useRouter();
  const { locale } = router;

  const { setBreadcrumbs } = useContext(
    BreadcrumbContext
  ) as IBreadcrumbContextType;

  const { t: tCommon } = useTranslation('common');
  const { t: tMeta } = useTranslation('meta');
  const { t: tLinks } = useTranslation('links');

  const [readSurahDetail, setReadSurahDetail] = useState<ReadSurah>(
    new ReadSurah(readSurah, locale)
  );

  const canonicalPath = replaceWords(routes.surahReadHistory, {
    suraSlug: readSurahDetail.slug,
  });

  const breadcrumbs: IBreadcrumb[] = [
    {
      title: tLinks('home'),
      path: routes.home,
    },
    {
      title: tLinks('surahs'),
      path: routes.surahRead,
    },
    {
      title: readSurahDetail.titleEn,
      path: `${routes.surahRead}/${readSurahDetail.slug}`,
    },
    {
      title: replaceWords(`${tCommon('historyOfSurah')}`, {
        title: readSurahDetail.titleEn,
      }),
      path: canonicalPath,
    },
  ];

  const canonicalURL = `${SITE_URL}${getLocaleForUrl(locale)}${canonicalPath}`;

  const htmlTitle = (): string => {
    return replaceWords(tMeta('historyOfSurah'), {
      transliteration: readSurah.transliteration,
    });
  };
  const htmlDescription = (): string => {
    return replaceWords(tMeta('historyOfSurahDescription'), {
      transliteration: readSurah.transliteration,
    });
  };

  const schema = baseMarkupSchema([
    webPageMarkupSchema(canonicalPath, htmlTitle(), htmlDescription()),
    webSiteMarkupSchema(),
    organizationMarkupSchema(),
    breadcrumbListMarkupSchema(breadcrumbs),
  ]);

  useEffect(() => {
    setReadSurahDetail(new ReadSurah(readSurah, locale));
  }, [data, locale]);

  useEffect(() => {
    setBreadcrumbs(breadcrumbs);
  }, []);

  return (
    <>
      <SeoHead
        title={htmlTitle()}
        description={htmlDescription()}
        canonicalURL={canonicalURL}
        schema={schema}
      />
      <main className={s['history']}>
        <AppTitle
          title={`${tCommon('historyOfSurah')} ${readSurahDetail.titleEn}`}
          main
        />
        <div className={s['header-wrapper']}>
          <AppContainer className={s['header']}>
            <div className={s['info']}>
              <Link href={`${routes.surahRead}/${readSurah.slug}`}>
                <IconButton>
                  <TurnLeftRounded />
                </IconButton>
              </Link>
              <AppTitle title={readSurahDetail.titleEn} />
            </div>
            <div className={s['case']}>
              <p>{tCommon('ayahs')}</p>
              <p>
                <span>{ayasCount}</span>
              </p>
            </div>
            <div className={s['case']}>
              <p>{tCommon('revelationPlace')}</p>
              <p>
                <span>{tCommon('mecca')}</span>
              </p>
            </div>
          </AppContainer>
        </div>

        <AppContainer className={s['content']}>
          <div>
            <div className={s['image']}>
              <Image
                className={s['image']}
                src="/images/surah.png"
                alt="surah"
                width={160}
                height={160}
                fetchPriority="high"
              />
            </div>
          </div>
          <div
            className={s['description']}
            dangerouslySetInnerHTML={{ __html: readSurahDetail.history }}
          />
        </AppContainer>
      </main>
    </>
  );
};

SurahReadHistory.getLayout = (page) => <MainLayout>{page}</MainLayout>;

export const getStaticPaths: GetStaticPaths = async () => {
  const { data: readSurahsResult } = await api.getReadSurahs(
    allRecordsPaginationOptions,
    ['slug']
  );
  const { results: readSurahs } = readSurahsResult;

  return {
    paths: readSurahs
      .map((surah: { slug: unknown }) => surah.slug)
      .filter(isValidSlug)
      .map((slug: string) => ({
        params: { slug },
      })),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  try {
    const { locale = '', params } = ctx;
    const { slug } = params || {};

    const { data } = await api.getReadSurahBySlug(String(slug));
    const { surah: readSurah, ayasCount } = data;

    const dataToEncrypt = {
      readSurah,
      ayasCount,
    };
    const { encryptPageProps } = await import('@/utils/crypto/page-props-encrypt');
    const encrypted = encryptPageProps(dataToEncrypt);

    return {
      props: {
        data: encrypted,
        ...(await serverSideTranslations(locale, localeList)),
      },
    };
  } catch (error) {
    console.error(error);
    return {
      notFound: true,
    };
  }
};

export default SurahReadHistory;
